import React, { memo, useEffect, useReducer } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Box, Typography } from '@mui/material'
import { ActivityLogButton, Button } from 'src/components/atoms'
import Breadcrumbs, {
  BreadcrumbsProps,
} from 'src/components/molecules/Breadcrumbs'
import { CheckFormType } from 'src/components/molecules/CheckBoxListWithFilter'
import { isOneOfRoles } from 'src/domain/role'
import { Member, RoleEnum, Store as EntityStore } from 'src/slices/services/api'
import listStyles from 'src/styles/list-styles.module.scss'
import MemberFilterBar from '../../components/FilterBar'
import MembersTable from '../../components/Table'
import { reducerFunc, INITIAL_STATE } from '../../reducer'
import { ActiveFilter } from '../../type'
import style from './styles.module.scss'

export type TemplatesMembersListProps = {
  orgCode: string
  members: Member[]
  breadCrumbPaths: BreadcrumbsProps['paths']
  stores: EntityStore[]
  role?: RoleEnum
  onClickCreateMember?: () => void
  onClickUpdateMember?: () => void
  onClickHistoryButton?: () => void
}

const TemplatesMembersList: React.StyledFC<TemplatesMembersListProps> = memo(
  ({
    orgCode,
    members,
    stores,
    breadCrumbPaths,
    role,
    onClickCreateMember,
    onClickUpdateMember,
    onClickHistoryButton,
  }: TemplatesMembersListProps) => {
    const [memberState, dispatch] = useReducer(reducerFunc, INITIAL_STATE)
    const filterformMethods = useForm()

    const handleSearchTextUpdated = (searchText: string) => {
      dispatch({
        type: 'UPDATE_SEARCH_TEXT',
        payload: { searchParamText: searchText },
      })
    }
    const handleRoleUpdated = (selectedRole: RoleEnum) => {
      dispatch({
        type: 'UPDATE_SEARCH_ROLE',
        payload: { role: selectedRole },
      })
    }
    const handleActiveUpdated = (active: ActiveFilter) => {
      dispatch({
        type: 'UPDATE_SEARCH_ACTIVE',
        payload: { active },
      })
    }
    const handleAssignedStoresUpdated = (assignedStores: CheckFormType) => {
      dispatch({
        type: 'UPDATE_ASSIGNED_STORE',
        payload: { assignedStores },
      })
    }

    useEffect(() => {
      dispatch({ type: 'UPDATE_MEMBERS', payload: { allMembers: members } })
    }, [members])

    return (
      <Box className={listStyles.container}>
        <Breadcrumbs className={style.breadcrumb} paths={breadCrumbPaths} />
        <Typography className={style.title} variant="h1">
          ユーザー管理
        </Typography>

        <Box className={style.filterWrapper}>
          <Box className={style.header}>
            <Box className={style.count}>{memberState.count} 人 </Box>
            <FormProvider {...filterformMethods}>
              <form className={style.filterForm}>
                <MemberFilterBar
                  orgCode={orgCode}
                  onSearchTextUpdated={handleSearchTextUpdated}
                  onRoleUpdated={handleRoleUpdated}
                  onActiveUpdated={handleActiveUpdated}
                  onAssignedStoresUpdated={handleAssignedStoresUpdated}
                  stores={stores}
                />
              </form>
            </FormProvider>
            {onClickHistoryButton && isOneOfRoles([RoleEnum.Admin], role) && (
              <ActivityLogButton
                className={style.historyLink}
                onClick={onClickHistoryButton}
              />
            )}
          </Box>
        </Box>

        <Box className={style.mainContent}>
          <MembersTable
            members={memberState.filteredMembers}
            orgCode={orgCode}
          />
        </Box>

        <Box className={style.bottom}>
          <Button
            title="ユーザー追加"
            size="medium"
            color="secondary"
            onClick={onClickCreateMember}
          />
          <Button
            disabled={memberState.filteredMembers?.length === 0}
            title="ユーザー編集"
            size="medium"
            color="primary"
            onClick={onClickUpdateMember}
          />
        </Box>
      </Box>
    )
  }
)

TemplatesMembersList.displayName = 'TemplatesMembers'
export default TemplatesMembersList
